import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import Modal from './Modal.jsx';
import Button from './Button.jsx';
import AiAssist from './AiAssist.jsx';

// Structuur › welkom: per function a welcome text and documents to read first; per
// commission a fixed contact person for newcomers (else the app picks one).

const ERRORS = {
  invalid_message: 'De welkomsttekst is te lang (max. 4000 tekens).',
  invalid_documents: 'Kies maximaal 10 documenten van deze club.',
  invalid_member: 'Kies een actief lid van de club.',
};
const errorText = err => ERRORS[err?.code] || 'Opslaan lukte niet.';

export function CategoryWelcomeModal({ cat, current, onClose, onSaved }) {
  const [message, setMessage] = useState('');
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!cat) return;
    setMessage(current?.message || ''); setDocs(current?.documents || []); setSearch(''); setResults([]); setError(null);
  }, [cat?.id]);

  useEffect(() => {
    if (!cat || search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api(`/api/documents?search=${encodeURIComponent(search.trim())}`).then(list => setResults(list.slice(0, 8))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, cat?.id]);

  async function save() {
    setBusy(true); setError(null);
    try {
      const saved = await api(`/api/welcome/settings/category/${cat.id}`, { method: 'PUT', body: JSON.stringify({ message, document_ids: docs.map(d => d.id) }) });
      onSaved(saved);
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  }

  const add = d => { if (!docs.some(x => x.id === d.id) && docs.length < 10) setDocs([...docs, { id: d.id, name: d.file_name, is_external: !!d.is_external }]); setSearch(''); };
  return (
    <Modal isOpen={!!cat} title={`Welkom — ${cat?.name || ''}`} onClose={() => !busy && onClose()}>
      <div style="display:flex;flex-direction:column;gap:14px">
        <div class="text-sm text-muted">Wie deze functie krijgt, ziet dit op zijn welkomstpagina, samen met het aanspreekpunt, de mappen en de collega's.</div>
        <div class="form-group" style="margin:0">
          <label class="form-label">Welkomsttekst</label>
          <textarea class="form-input" rows={4} maxLength={4000} value={message} onInput={e => setMessage(e.target.value)}
            placeholder="Bijv. wat de functie inhoudt, wanneer de commissie vergadert, wat je in je eerste weken doet…" />
          <AiAssist kind="welcome_message" value={message} context={{ function: cat?.name || '' }} onUse={setMessage} />
        </div>
        <div class="form-group" style="margin:0">
          <label class="form-label">Lees eerst (max. 10)</label>
          {docs.length > 0 && (
            <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:8px">
              {docs.map(d => (
                <div key={d.id} style="display:flex;gap:8px;align-items:center;font-size:13px">
                  <span style="flex:1;min-width:0;overflow-wrap:anywhere">{d.is_external ? '🔗' : '📄'} {d.name || <em class="text-muted">Document dat jij niet kunt zien</em>}</span>
                  <button type="button" style="border:none;background:none;cursor:pointer;color:var(--color-danger,#dc2626)" title="Weghalen" onClick={() => setDocs(docs.filter(x => x.id !== d.id))}>✕</button>
                </div>
              ))}
            </div>
          )}
          <input class="form-input" value={search} onInput={e => setSearch(e.target.value)} placeholder="Zoek een document…" aria-label="Zoek een document" />
          {results.length > 0 && (
            <div style="border:1px solid var(--color-border);border-radius:8px;margin-top:4px;overflow:hidden">
              {results.map(d => (
                <button key={d.id} type="button" onClick={() => add(d)}
                  style="display:block;width:100%;text-align:left;border:none;border-bottom:1px solid var(--color-border);background:var(--color-bg);padding:6px 10px;font-size:13px;cursor:pointer;color:var(--color-text)">
                  {d.is_external ? '🔗' : '📄'} {d.file_name}{d.category ? <span class="text-muted"> · {d.category}</span> : ''}
                </button>
              ))}
            </div>
          )}
          <div class="text-sm text-muted" style="margin-top:4px">Iemand ziet alleen de documenten die hij mag openen. Komt er een nieuwe versie, dan krijgt hij die.</div>
        </div>
        {error && <div class="alert alert-error">{error}</div>}
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Bezig…' : 'Opslaan'}</Button>
        </div>
      </div>
    </Modal>
  );
}

export function CommissionContactModal({ commission, current, onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!commission) return;
    setUserId(current?.user_id ? String(current.user_id) : ''); setError(null);
    api('/api/users/members').then(setMembers).catch(() => {});
  }, [commission?.id]);

  async function save() {
    setBusy(true); setError(null);
    try {
      const { contact } = await api(`/api/welcome/settings/commission/${commission.id}`, { method: 'PUT', body: JSON.stringify({ user_id: userId ? Number(userId) : null }) });
      onSaved(contact);
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  }

  return (
    <Modal isOpen={!!commission} title={`Aanspreekpunt — ${commission?.name || ''}`} onClose={() => !busy && onClose()}>
      <div style="display:flex;flex-direction:column;gap:14px">
        <div class="text-sm text-muted">Nieuwe leden van deze commissie en nieuwe vrijwilligers voor haar vacatures (zonder coördinator) krijgen dit aanspreekpunt. Leeg: de app kiest de voorzitter van het gremium, anders wie toezicht houdt op de commissie, anders wie de functie gaf.</div>
        <select class="form-select" value={userId} onChange={e => setUserId(e.target.value)} aria-label="Aanspreekpunt">
          <option value="">— Automatisch —</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
        {error && <div class="alert alert-error">{error}</div>}
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Bezig…' : 'Opslaan'}</Button>
        </div>
      </div>
    </Modal>
  );
}
